/**
 * SoftSkillsProtocolsPage — v10.7.3
 * Admin UI para configurar protocolos de habilidades blandas (SPIKES, NURSE, etc.).
 *
 * Permite editar:
 *   - Pesos de rúbrica (completion + quality)
 *   - Etapas requeridas (subset del stages)
 *   - Stages disponibles (id, name, label)
 */
import { useEffect, useState } from 'react';
import api from '../../api';
import { toast } from 'sonner';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { ChatCircle, Plus, Trash, FloppyDisk, ArrowUp, ArrowDown } from '@phosphor-icons/react';

const EMPTY = () => ({
  code: '',
  name: '',
  description: '',
  language: 'es',
  version: 1,
  stages: [],
  weights: { completion: 60, quality: 40 },
  required_stages: [],
  editable: true,
});

export default function SoftSkillsProtocolsPage() {
  const [protocols, setProtocols] = useState([]);
  const [selectedCode, setSelectedCode] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/soft-skills/protocols');
      setProtocols(data.protocols || []);
      if ((data.protocols || []).length && !selectedCode) {
        setSelectedCode(data.protocols[0].code);
        setDraft(structuredClone(data.protocols[0]));
      }
    } catch {
      toast.error('No se pudieron cargar los protocolos');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const selectProtocol = (p) => {
    setSelectedCode(p.code);
    setDraft(structuredClone(p));
  };

  const newProtocol = () => {
    const d = EMPTY();
    d.code = 'NUEVO';
    d.name = 'Nuevo protocolo';
    setSelectedCode(null);
    setDraft(d);
  };

  const save = async () => {
    if (!draft?.code) return toast.error('Falta el código');
    setSaving(true);
    try {
      const w = draft.weights || {};
      const sum = (Number(w.completion) || 0) + (Number(w.quality) || 0);
      if (Math.abs(sum - 100) > 0.5) {
        toast.error('Los pesos deben sumar 100');
        setSaving(false);
        return;
      }
      await api.post('/soft-skills/protocols', draft);
      toast.success('Protocolo guardado');
      await load();
      setSelectedCode(draft.code);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!draft?.code || draft.code === 'SPIKES') return;
    if (!window.confirm(`¿Eliminar protocolo ${draft.code}?`)) return;
    try {
      await api.delete(`/soft-skills/protocols/${draft.code}`);
      toast.success('Protocolo eliminado');
      setDraft(null);
      setSelectedCode(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al eliminar');
    }
  };

  const setField = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const setWeight = (k, v) => setDraft((d) => ({ ...d, weights: { ...(d.weights || {}), [k]: Number(v) || 0 } }));

  const addStage = () => {
    setDraft((d) => ({
      ...d,
      stages: [...(d.stages || []), { id: '', name: '', label: '', order: (d.stages || []).length, required: true }],
    }));
  };
  const removeStage = (idx) => {
    setDraft((d) => {
      const stages = [...(d.stages || [])];
      const removed = stages.splice(idx, 1)[0];
      stages.forEach((s, i) => (s.order = i));
      const required_stages = (d.required_stages || []).filter((c) => c !== removed?.id);
      return { ...d, stages, required_stages };
    });
  };
  const moveStage = (idx, dir) => {
    setDraft((d) => {
      const stages = [...(d.stages || [])];
      const to = idx + dir;
      if (to < 0 || to >= stages.length) return d;
      [stages[idx], stages[to]] = [stages[to], stages[idx]];
      stages.forEach((s, i) => (s.order = i));
      return { ...d, stages };
    });
  };
  const updateStage = (idx, field, value) => {
    setDraft((d) => {
      const stages = [...(d.stages || [])];
      stages[idx] = { ...stages[idx], [field]: value };
      return { ...d, stages };
    });
  };
  const toggleRequired = (stageId) => {
    setDraft((d) => {
      const req = new Set(d.required_stages || []);
      if (req.has(stageId)) req.delete(stageId); else req.add(stageId);
      return { ...d, required_stages: Array.from(req) };
    });
  };

  return (
    <div className="space-y-6" data-testid="soft-skills-protocols-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <ChatCircle size={26} className="text-purple-600" weight="fill" />
            Protocolos Habilidades Blandas
          </h1>
          <p className="text-sm text-slate-500 mt-1">Rúbricas configurables: pesos, etapas y requeridas.</p>
        </div>
        <Button onClick={newProtocol} data-testid="new-protocol-btn"><Plus size={16} className="mr-1" />Nuevo protocolo</Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Lista */}
        <Card className="md:col-span-1">
          <CardContent className="p-3 space-y-1" data-testid="protocol-list">
            {loading && <div className="text-slate-500 text-sm p-2">Cargando…</div>}
            {!loading && !protocols.length && <div className="text-slate-500 text-sm p-2">Sin protocolos aún.</div>}
            {protocols.map((p) => (
              <button
                key={p.code}
                onClick={() => selectProtocol(p)}
                className={`w-full text-left p-3 rounded-md transition-all ${selectedCode === p.code ? 'bg-purple-50 border border-purple-200' : 'hover:bg-slate-50 border border-transparent'}`}
                data-testid={`protocol-item-${p.code}`}
              >
                <div className="font-semibold text-sm text-slate-900">{p.code}</div>
                <div className="text-xs text-slate-500 truncate">{p.name}</div>
                <div className="text-[10px] text-slate-400 mt-1">{(p.stages || []).length} etapas · v{p.version || 1}</div>
              </button>
            ))}
          </CardContent>
        </Card>

        {/* Editor */}
        <Card className="md:col-span-3">
          <CardContent className="p-5 space-y-5">
            {!draft ? (
              <div className="text-slate-500 text-sm">Selecciona un protocolo o crea uno nuevo.</div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Código</Label>
                    <Input value={draft.code || ''} onChange={(e) => setField('code', e.target.value.toUpperCase().replace(/\s+/g, '_'))} data-testid="protocol-code-input" />
                  </div>
                  <div>
                    <Label>Nombre</Label>
                    <Input value={draft.name || ''} onChange={(e) => setField('name', e.target.value)} data-testid="protocol-name-input" />
                  </div>
                </div>
                <div>
                  <Label>Descripción</Label>
                  <Textarea rows={2} value={draft.description || ''} onChange={(e) => setField('description', e.target.value)} data-testid="protocol-desc-input" />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Peso completitud (%)</Label>
                    <Input type="number" min={0} max={100} value={draft.weights?.completion ?? 60} onChange={(e) => setWeight('completion', e.target.value)} data-testid="weight-completion" />
                  </div>
                  <div>
                    <Label>Peso calidad (%)</Label>
                    <Input type="number" min={0} max={100} value={draft.weights?.quality ?? 40} onChange={(e) => setWeight('quality', e.target.value)} data-testid="weight-quality" />
                  </div>
                </div>
                <div className="text-xs text-slate-500">
                  Suma: <strong className={`${((Number(draft.weights?.completion) || 0) + (Number(draft.weights?.quality) || 0)) === 100 ? 'text-emerald-600' : 'text-red-600'}`}>
                    {(Number(draft.weights?.completion) || 0) + (Number(draft.weights?.quality) || 0)}%
                  </strong> (debe ser 100)
                </div>

                <div className="border-t border-slate-100 pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <Label>Etapas del protocolo</Label>
                    <Button size="sm" variant="outline" onClick={addStage} data-testid="add-stage-btn"><Plus size={14} className="mr-1" />Añadir etapa</Button>
                  </div>
                  <div className="space-y-2">
                    {(draft.stages || []).map((s, idx) => (
                      <div key={idx} className="grid grid-cols-12 gap-2 items-end p-3 rounded-md bg-slate-50 border border-slate-100" data-testid={`stage-row-${idx}`}>
                        <div className="col-span-2">
                          <Label className="text-[10px]">ID</Label>
                          <Input value={s.id || ''} onChange={(e) => updateStage(idx, 'id', e.target.value)} placeholder="S" data-testid={`stage-id-${idx}`} />
                        </div>
                        <div className="col-span-3">
                          <Label className="text-[10px]">Nombre</Label>
                          <Input value={s.name || ''} onChange={(e) => updateStage(idx, 'name', e.target.value)} placeholder="Setting" />
                        </div>
                        <div className="col-span-4">
                          <Label className="text-[10px]">Etiqueta</Label>
                          <Input value={s.label || ''} onChange={(e) => updateStage(idx, 'label', e.target.value)} placeholder="Preparar entorno" />
                        </div>
                        <div className="col-span-2 flex flex-col items-start">
                          <Label className="text-[10px]">Requerida</Label>
                          <Badge
                            className={`cursor-pointer mt-1 ${(draft.required_stages || []).includes(s.id) ? 'bg-emerald-100 text-emerald-700 border-emerald-200' : 'bg-slate-100 text-slate-500 border-slate-200'}`}
                            onClick={() => s.id && toggleRequired(s.id)}
                            data-testid={`required-toggle-${idx}`}
                          >
                            {(draft.required_stages || []).includes(s.id) ? 'Sí' : 'No'}
                          </Badge>
                        </div>
                        <div className="col-span-1 flex flex-col gap-1">
                          <Button size="icon" variant="ghost" onClick={() => moveStage(idx, -1)} disabled={idx === 0} className="h-6 w-6">
                            <ArrowUp size={12} />
                          </Button>
                          <Button size="icon" variant="ghost" onClick={() => moveStage(idx, 1)} disabled={idx === (draft.stages || []).length - 1} className="h-6 w-6">
                            <ArrowDown size={12} />
                          </Button>
                          <Button size="icon" variant="ghost" onClick={() => removeStage(idx)} className="h-6 w-6 text-red-600" data-testid={`remove-stage-${idx}`}>
                            <Trash size={12} />
                          </Button>
                        </div>
                      </div>
                    ))}
                    {!(draft.stages || []).length && <div className="text-xs text-slate-400 text-center py-4">Sin etapas aún. Añade al menos una.</div>}
                  </div>
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                  <Button
                    variant="ghost"
                    className="text-red-600"
                    onClick={remove}
                    disabled={!draft.code || draft.code === 'SPIKES'}
                    data-testid="delete-protocol-btn"
                  >
                    <Trash size={16} className="mr-1" />Eliminar
                  </Button>
                  <Button onClick={save} disabled={saving} data-testid="save-protocol-btn">
                    <FloppyDisk size={16} className="mr-1" />{saving ? 'Guardando…' : 'Guardar'}
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
